/* MedLora / Learning Edit — browser animations and Shopify editor-safe interactions.
 * Node.js/Shopify CLI are local development tools, not storefront animation runtimes.
 */
(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  ready(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motionEnabled = document.documentElement.dataset.motion !== 'off' && !reducedMotion.matches;
    // The Liquid layout sets data-motion in the theme settings (see below).
    document.documentElement.classList.remove('no-js');

    const progress = document.querySelector('[data-scroll-progress]');
    let scrollScheduled = false;
    const updateScroll = () => {
      const y = window.scrollY || 0;
      document.querySelector('[data-header]')?.classList.toggle('is-scrolled', y > 28);
      if (progress) {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        progress.style.width = `${scrollable > 0 ? Math.min(100, y / scrollable * 100) : 0}%`;
      }
      scrollScheduled = false;
    };
    window.addEventListener('scroll', () => {
      if (!scrollScheduled) { scrollScheduled = true; window.requestAnimationFrame(updateScroll); }
    }, { passive: true });
    updateScroll();

    // Shopify editor re-renders sections without a full-page reload. All initializers
    // below are idempotent, so new section markup gets its event handlers too.
    const scoped = (root, selector) => [
      ...(root.matches?.(selector) ? [root] : []),
      ...root.querySelectorAll(selector)
    ];

    const initMenu = (root) => {
      scoped(root, '[data-header]').forEach((header) => {
        const toggle = header.querySelector('[data-menu-toggle]');
        const mobileMenu = header.querySelector('#MobileMenu');
        if (!toggle || !mobileMenu || toggle.dataset.medloraMenuReady) return;
        toggle.dataset.medloraMenuReady = 'true';
        const setMenu = (open) => {
          mobileMenu.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
          toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        };
        toggle.addEventListener('click', () => setMenu(mobileMenu.hidden));
        mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
        document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setMenu(false); });
        const desktop = window.matchMedia('(min-width: 901px)');
        desktop.addEventListener?.('change', (event) => { if (event.matches) setMenu(false); });
      });
    };

    const revealObserver = motionEnabled && 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: '0px 0px -25px 0px', threshold: 0.07 })
      : null;

    const initReveals = (root) => {
      const elements = scoped(root, '.reveal');
      if (!revealObserver) {
        elements.forEach((element) => {
          element.classList.remove('reveal-pending');
          element.classList.add('is-visible');
        });
        return;
      }
      document.documentElement.classList.add('motion-ready');
      elements.forEach((element) => {
        if (element.dataset.medloraRevealReady) return;
        element.dataset.medloraRevealReady = 'true';
        // Never conceal content above the viewport or inside editor-selected sections.
        if (element.getBoundingClientRect().bottom < 0 || (ShopifyEditor && element.closest('.shopify-section.shopify-section--selected'))) {
          element.classList.add('is-visible');
          return;
        }
        element.classList.add('reveal-pending');
        revealObserver.observe(element);
      });
    };

    const ShopifyEditor = Boolean(window.Shopify?.designMode);
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const initTilt = (root) => {
      if (!motionEnabled || document.documentElement.dataset.pointerTilt === 'off' || !finePointer) return;
      scoped(root, '[data-tilt]').forEach((element) => {
        if (element.dataset.medloraTiltReady) return;
        element.dataset.medloraTiltReady = 'true';
        let frame = 0;
        let x = 0;
        let y = 0;
        element.addEventListener('pointermove', (event) => {
          const rect = element.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          x = ((event.clientX - rect.left) / rect.width - .5) * 6;
          y = ((event.clientY - rect.top) / rect.height - .5) * -6;
          if (!frame) frame = window.requestAnimationFrame(() => {
            element.style.transform = `perspective(1100px) rotateX(${y.toFixed(2)}deg) rotateY(${x.toFixed(2)}deg)`;
            frame = 0;
          });
        }, { passive: true });
        element.addEventListener('pointerleave', () => {
          if (frame) window.cancelAnimationFrame(frame);
          frame = 0;
          element.style.transform = '';
        });
      });
    };

    const initAutoSubmit = (root) => scoped(root, '[data-auto-submit]').forEach((select) => {
      if (select.dataset.medloraAutoReady) return;
      select.dataset.medloraAutoReady = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });

    // Product gallery, native accessible image dialog, and variant state.
    // Each section is initialized exactly once, including editor-reloaded sections.
    const initProducts = (root) => scoped(root, '[data-product-section]').forEach((section) => {
      if (section.dataset.medloraProductReady) return;
      section.dataset.medloraProductReady = 'true';
      const mainImage = section.querySelector('[data-gallery-main] img');
      const counter = section.querySelector('[data-gallery-count]');
      const thumbnails = [...section.querySelectorAll('[data-gallery-thumb]')];
      const zoom = section.querySelector('[data-gallery-zoom]');
      const dialog = section.querySelector('[data-product-dialog]');
      const dialogImage = section.querySelector('[data-gallery-dialog-image]');
      const closeDialog = section.querySelector('[data-gallery-close]');
      const animationEnabled = motionEnabled;
      const setGalleryImage = (url, alt, id, index) => {
        if (!mainImage || !url) return;
        if (mainImage.src !== new URL(url, window.location.href).href) {
          mainImage.src = url;
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          if (animationEnabled) {
            const art = section.querySelector('[data-gallery-main]');
            art?.classList.remove('is-switching');
            // Restart CSS transition for images chosen from variant selector or filmstrip.
            if (art) { void art.offsetWidth; art.classList.add('is-switching'); }
          }
        }
        mainImage.alt = alt || '';
        if (dialogImage) { dialogImage.src = url; dialogImage.alt = alt || ''; }
        const matched = thumbnails.find((thumb) => thumb.dataset.galleryId === String(id)) ||
          thumbnails.find((thumb) => thumb.dataset.gallerySrc === url);
        thumbnails.forEach((thumb) => {
          const active = thumb === matched;
          thumb.classList.toggle('is-current', active);
          thumb.setAttribute('aria-pressed', String(active));
        });
        const number = index || Number(matched?.dataset.galleryIndex) || 1;
        if (counter) counter.textContent = `${String(number).padStart(2, '0')} / ${String(Math.max(1, thumbnails.length)).padStart(2, '0')}`;
      };
      thumbnails.forEach((thumb) => thumb.addEventListener('click', () => {
        setGalleryImage(thumb.dataset.gallerySrc, thumb.dataset.galleryAlt, thumb.dataset.galleryId, Number(thumb.dataset.galleryIndex));
      }));
      if (zoom && dialog && typeof dialog.showModal === 'function') {
        zoom.addEventListener('click', () => {
          if (mainImage && dialogImage) { dialogImage.src = mainImage.currentSrc || mainImage.src; dialogImage.alt = mainImage.alt; }
          dialog.showModal();
          closeDialog?.focus();
        });
        closeDialog?.addEventListener('click', () => { dialog.close(); zoom.focus(); });
        dialog.addEventListener('click', (event) => { if (event.target === dialog) { dialog.close(); zoom.focus(); } });
      } else if (zoom) zoom.hidden = true;

      const select = section.querySelector('[data-variant-select]');
      const data = section.querySelector('[data-product-variants]');
      const formatData = section.querySelector('[data-money-format]');
      const price = section.querySelector('[data-product-price]');
      const sale = section.querySelector('[data-product-sale]');
      const submit = section.querySelector('[data-add-to-cart]');
      const submitLabel = section.querySelector('[data-add-label]');
      if (!select || !data || !formatData) return;
      let variants, moneyFormat;
      try { variants = JSON.parse(data.textContent); moneyFormat = JSON.parse(formatData.textContent); }
      catch (_) { return; }
      const formatMoney = (cents) => {
        const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!placeholder) return (cents / 100).toFixed(2);
        const kind = placeholder[1];
        const noDecimals = kind.includes('no_decimals');
        const useComma = kind.includes('comma_separator');
        const useSpace = kind.includes('space_separator');
        const useApostrophe = kind.includes('apostrophe_separator');
        const decimal = useComma ? ',' : '.';
        const grouping = useComma ? '.' : useSpace ? ' ' : useApostrophe ? "'" : ',';
        const parts = (cents / 100).toFixed(noDecimals ? 0 : 2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, grouping);
        return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/g, parts.join(decimal)).replace(/<[^>]*>/g, '');
      };
      select.addEventListener('change', () => {
        const variant = variants.find((entry) => String(entry.id) === select.value);
        if (!variant) return;
        if (price) {
          const current = document.createElement('span');
          current.textContent = formatMoney(variant.price);
          price.replaceChildren(current);
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            const previous = document.createElement('s');
            previous.textContent = formatMoney(variant.compare_at_price);
            price.append(previous);
          }
        }
        if (sale) sale.hidden = !(variant.compare_at_price && variant.compare_at_price > variant.price);
        if (submit) submit.disabled = !variant.available;
        if (submitLabel) submitLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
        const option = select.selectedOptions[0];
        if (option?.dataset.imageSrc) setGalleryImage(option.dataset.imageSrc, option.dataset.imageAlt, option.dataset.imageId);
        if (window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url);
        }
      });
    });

    // Accessible, editor-safe carousel controls. The rail itself works without JS via touch/trackpad scrolling.
    const initRails = (root) => {
      scoped(root, '[data-spotlight]').forEach((section) => {
        const track = section.querySelector('[data-rail-track]');
        const previous = section.querySelector('[data-rail-prev]');
        const next = section.querySelector('[data-rail-next]');
        if (!track || !previous || !next || section.dataset.medloraRailReady) return;
        section.dataset.medloraRailReady = 'true';
        const updateButtons = () => {
          previous.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        };
        const slide = (direction) => {
          const card = track.querySelector('.ml-spotlight__item');
          const width = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * .8;
          track.scrollBy({ left: width * direction, behavior: motionEnabled ? 'smooth' : 'auto' });
        };
        previous.addEventListener('click', () => slide(-1));
        next.addEventListener('click', () => slide(1));
        track.addEventListener('scroll', updateButtons, { passive: true });
        window.addEventListener('resize', updateButtons, { passive: true });
        updateButtons();
      });
    };

    const initSection = (root) => {
      initMenu(root);
      initReveals(root);
      initTilt(root);
      initAutoSubmit(root);
      initProducts(root);
      initRails(root);
      updateScroll();
    };
    initSection(document);
    document.addEventListener('shopify:section:load', (event) => initSection(event.target));
    // Reveal the actively edited section/block even if it was offscreen before selection.
    const showSelected = (event) => {
      scoped(event.target, '.reveal').forEach((element) => {
        element.classList.add('is-visible');
        revealObserver?.unobserve(element);
      });
      event.target.closest('.reveal')?.classList.add('is-visible');
    };
    document.addEventListener('shopify:section:select', showSelected);
    document.addEventListener('shopify:block:select', showSelected);
  });
})();
